import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/auth-store'
import { useDevicesStore } from '@/stores/devices-store'
import { AlertCircle, Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

interface BasicSettingsForm {
    assistentName: string
    smsSender: string
    persona: string
}

const AVAILABLE_PERSONAS = [
    { id: 'FRIENDLY', displayTitle: 'Freundlich & Hilfsbereit' },
    { id: 'PROFESSIONAL', displayTitle: 'Professionell & Sachlich' },
    { id: 'CASUAL', displayTitle: 'Locker & Entspannt' },
    { id: 'FORMAL', displayTitle: 'Formell & Höflich' },
]

export function BasicSettings() {
    const { currentDevice, updateConfigData, getData } = useDevicesStore()
    const { canEdit } = useAuthStore()
    const { toast } = useToast()
    const [isLoading, setIsLoading] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        setValue,
        watch,
    } = useForm<BasicSettingsForm>({
        defaultValues: {
            assistentName: '',
            smsSender: '',
            persona: 'FRIENDLY',
        },
    })

    const personaValue = watch('persona')

    useEffect(() => {
        if (currentDevice?.deviceId) {
            loadData()
        }
    }, [currentDevice?.deviceId])

    const loadData = () => {
        if (!currentDevice) return

        const assistentName = getData('ASSISTENT_NAME')
        const smsSender = getData('SMS_SENDER')
        const persona = getData('PERSONA')

        reset({
            assistentName: (typeof assistentName === 'string' ? assistentName : null) || currentDevice.customerName || '',
            smsSender: (typeof smsSender === 'string' ? smsSender : null) || '',
            persona: (typeof persona === 'string' ? persona : null) || 'FRIENDLY',
        })
    }

    const onSubmit = async (data: BasicSettingsForm) => {
        if (!isDirty) return

        setIsLoading(true)

        try {
            await Promise.all([
                updateConfigData('ASSISTENT_NAME', data.assistentName),
                updateConfigData('SMS_SENDER', data.smsSender),
                updateConfigData('PERSONA', data.persona),
            ])

            toast({
                title: 'Erfolgreich gespeichert',
                description: 'Die Grundeinstellungen wurden aktualisiert',
            })

            reset(data)
        } catch (error) {
            console.error('Error saving basic settings:', error)
            toast({
                variant: 'destructive',
                title: 'Fehler',
                description: 'Die Einstellungen konnten nicht gespeichert werden',
            })
        } finally {
            setIsLoading(false)
        }
    }

    const isDisabled = !canEdit() || isLoading

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Assistant Name */}
            <div className="space-y-2">
                <Label htmlFor="assistentName" className="text-sm font-semibold text-gray-700">
                    Assistent-Name
                    <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                    id="assistentName"
                    {...register('assistentName', {
                        required: 'Bitte geben Sie einen Namen ein',
                        minLength: { value: 2, message: 'Name muss mindestens 2 Zeichen lang sein' },
                    })}
                    placeholder="z.B. Restaurant-Assistent"
                    disabled={isDisabled}
                    className={errors.assistentName ? 'border-red-300' : ''}
                />
                {errors.assistentName && (
                    <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.assistentName.message}
                    </p>
                )}
                <p className="text-xs text-gray-500">
                    Der Name wird in Anrufen und Nachrichten verwendet
                </p>
            </div>

            {/* SMS Sender */}
            <div className="space-y-2">
                <Label htmlFor="smsSender" className="text-sm font-semibold text-gray-700">
                    SMS-Absendername
                    <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                    id="smsSender"
                    {...register('smsSender', {
                        required: 'Bitte geben Sie einen Absendernamen ein',
                        maxLength: { value: 11, message: 'Maximal 11 Zeichen erlaubt' },
                    })}
                    placeholder="z.B. Restaurant"
                    disabled={isDisabled}
                    className={errors.smsSender ? 'border-red-300' : ''}
                    maxLength={11}
                />
                {errors.smsSender && (
                    <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.smsSender.message}
                    </p>
                )}
                <p className="text-xs text-gray-500">
                    Wird als Absender bei SMS-Nachrichten angezeigt (max. 11 Zeichen)
                </p>
            </div>

            {/* Persona */}
            <div className="space-y-2">
                <Label htmlFor="persona" className="text-sm font-semibold text-gray-700">
                    Kommunikations-Stil (Persona)
                </Label>
                <Select
                    value={personaValue}
                    onValueChange={(value) => setValue('persona', value, { shouldDirty: true })}
                    disabled={isDisabled}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Stil auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                        {AVAILABLE_PERSONAS.map((persona) => (
                            <SelectItem key={persona.id} value={persona.id}>
                                {persona.displayTitle}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                    Bestimmt den Ton und Stil der Kommunikation mit Gästen
                </p>
            </div>

            {/* Info Note */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                    <strong>Tipp:</strong> Diese Einstellungen beeinflussen, wie Ihr Assistent mit Gästen
                    kommuniziert. Wählen Sie einen Stil, der zu Ihrem Restaurant passt.
                </p>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
                <Button
                    type="submit"
                    disabled={!isDirty || isDisabled}
                    className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90"
                >
                    <Check className="w-4 w-4" />
                    {isLoading ? 'Speichert...' : 'Änderungen speichern'}
                </Button>
            </div>
        </form>
    )
}
